import { useCallback } from 'react';

import { useSessionValue, useClearSession } from 'appLayer/auth';
import { fetcher, FetcherError, RequestPayload } from 'shared/utils/fetcher';
import type { HttpMethods } from 'api/types';

interface Options {
  method: HttpMethods;
  url: string;
  data?: RequestPayload;
  shouldTimeout?: boolean;
  timeoutInMs?: number;
}

export const useInternalApiRequest = () => {
  const session = useSessionValue();
  const clearSession = useClearSession();

  return useCallback(
    async <T = unknown>({ method, url, data, shouldTimeout, timeoutInMs }: Options) => {
      try {
        const accessToken = session?.accessToken;

        if (!accessToken) {
          const error = new Error('No internal access token found');
          error.name = 'InternalAccessTokenError';
          throw error;
        }

        let signal;

        if ((shouldTimeout || Boolean(timeoutInMs)) && AbortSignal?.timeout) {
          signal = AbortSignal.timeout(timeoutInMs ?? 7_500);
        }

        const response = await fetcher[method.toLowerCase()]<T>(url, data, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          },
          signal
        });

        if (signal?.aborted) throw new Error(`${method} Request to ${url} timed out`);

        return response.data;
      } catch (error) {
        if (
          (error instanceof FetcherError && error.status === 401) ||
          (error instanceof Error && error.name === 'InternalAccessTokenError')
        ) {
          clearSession();
        }

        throw error;
      }
    },
    [clearSession, session]
  );
};
